import { Controller } from "@hotwired/stimulus"

// Handles search functionality for filter groups and communicates with filter-state controller
// Focuses on search UI and selection - state management handled by filter_state_controller
// Usage: data-controller="filter-search" data-filter-search-type-value="topics"
export default class extends Controller {
  static values = { 
    type: String,        // "topics", "certifications", or "providers"
    url: String          // Search endpoint URL
  }
  static targets = ["input", "checkbox", "hiddenInput", "clearButton"]

  connect() {
    this.searchTimeout = null
    this.selectedSet = new Set()
    
    // Find the filter state controller
    this.stateController = this.getStateController()
    
    // Listen for state changes from the filter state controller
    this.boundStateChanged = this.stateChanged.bind(this)
    this.element.addEventListener('filter-state:state-changed', this.boundStateChanged)
    
    // Listen for Turbo frame load events
    this.turboFrameLoadHandler = () => this.initializeSelectedSet()
    document.addEventListener('turbo:frame-load', this.turboFrameLoadHandler)
    
    // Initialize from current state
    this.initializeSelectedSet()
    this.updateClearButtonVisibility()
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.turboFrameLoadHandler)
    this.element.removeEventListener('filter-state:state-changed', this.boundStateChanged)
  }

  // Get the filter state controller instance
  getStateController() {
    const stateElement = this.element.closest('[data-controller*="filter-state"]')
    return this.application.getControllerForElementAndIdentifier(stateElement, "filter-state")
  }

  // Map controller type to URL parameter name (e.g., "topics" -> "topic")
  get parameterName() {
    return this.typeValue.replace(/s$/, '')
  }

  initializeSelectedSet() {
    // Initialize from filter state controller if available
    if (this.stateController) {
      const values = this.stateController.getFilter(this.parameterName)
      this.selectedSet.clear()
      values.forEach(value => this.selectedSet.add(value))
    } else {
      // Fallback: initialize from hidden input (for initial page load)
      if (this.hasHiddenInputTarget) {
        const value = this.hiddenInputTarget.value
        this.selectedSet.clear()
        if (value) {
          value.split(',').forEach(v => {
            if (v.trim()) this.selectedSet.add(v.trim())
          })
        }
      }
    }
    
    // Update UI to match selectedSet
    this.updateHiddenInput()
    this.updateCheckboxes()
  }

  // Handle checkbox toggle
  toggle(event) {
    const cb = event.target
    if (cb.checked) {
      this.selectedSet.add(cb.value)
    } else {
      this.selectedSet.delete(cb.value)
    }
    
    // Update hidden input immediately
    this.updateHiddenInput()
    
    // Tell state controller about the change
    if (this.stateController) {
      this.stateController.updateFilter(this.parameterName, Array.from(this.selectedSet))
    }
    
    // If there is a search query, re-run the search with the updated selection
    if (this.hasInputTarget && this.inputTarget.value.trim().length >= 2) {
      this.search()
    }
  }

  // Handle state changes from filter state controller
  stateChanged(event) {
    const { state } = event.detail
    const values = state[this.parameterName] || []
    
    // Update selectedSet to match state
    this.selectedSet.clear()
    values.forEach(value => this.selectedSet.add(value))
    
    // Update UI
    this.updateHiddenInput()
    this.updateCheckboxes()
  }

  // Update hidden input value
  updateHiddenInput() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = Array.from(this.selectedSet).join(',')
    }
  }

  // Update checkbox states to match selectedSet
  updateCheckboxes() {
    this.checkboxTargets.forEach(cb => {
      cb.checked = this.selectedSet.has(cb.value)
    })
  }

  // Search functionality
  search() {
    const query = this.inputTarget.value.trim()
    this.updateClearButtonVisibility()
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    if (query.length < 2) {
      this.showPopular()
      return
    }
    
    // Debounce by 200ms for snappier UX
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 200)
  }

  async performSearch(query) {
    const selectedValues = Array.from(this.selectedSet)
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('q', query)
    if (selectedValues.length > 0) {
      url.searchParams.set('selected', selectedValues.join(','))
    }
    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Search request failed:', error)
    }
  }

  async showPopular() {
    const selectedValues = Array.from(this.selectedSet)
    const url = new URL(this.urlValue, window.location.origin)
    if (selectedValues.length > 0) {
      url.searchParams.set('selected', selectedValues.join(','))
    }
    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Popular items request failed:', error)
    }
  }

  getSelectedValues() {
    return Array.from(this.selectedSet)
  }

  clearSearch() {
    if (this.inputTarget.value.trim().length > 0) {
      this.inputTarget.value = ''
      this.updateClearButtonVisibility()
      this.showPopular()
    }
  }

  updateClearButtonVisibility() {
    if (this.hasClearButtonTarget) {
      const hasText = this.inputTarget.value.trim().length > 0
      this.clearButtonTarget.style.display = hasText ? 'block' : 'none'
    }
  }
}